import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
// import { useSWRConfig } from 'swr';
// import { io, Socket } from 'socket.io-client';
// import { authService } from '../services/authService';
// import { SOCKET_IO_URL, SOCKET_IO_PATH, SOCKET_IO_TRANSPORTS } from '../config/apiConfig';
import { ChatNotificationToast, type ChatNotificationItem } from '../modules/chat/components/ChatNotificationToast';

interface ChatContextType {
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  selectedChannelId: number | null;
  setSelectedChannel: (id: number | null) => void;
  joinChannel: (channelId: number) => void;
  leaveChannel: (channelId: number) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

function senderNameFromPayload(payload: any): string {
  const s = payload?.sender;
  if (!s) return 'Someone';
  return (
    s.userDisplayName ||
    [s.userFirstName, s.userLastName].filter(Boolean).join(' ') ||
    'Someone'
  );
}

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedChannelId, setSelectedChannelIdState] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<ChatNotificationItem[]>([]);
  // const socketRef = useRef<Socket | null>(null);
  const isPanelOpenRef = useRef(isPanelOpen);
  const selectedChannelIdRef = useRef(selectedChannelId);
  // const { mutate: globalMutate } = useSWRConfig(); // used when Socket.IO is enabled

  useEffect(() => {
    isPanelOpenRef.current = isPanelOpen;
    selectedChannelIdRef.current = selectedChannelId;
  }, [isPanelOpen, selectedChannelId]);

  /* Socket.IO disabled for now; re-enable by uncommenting imports and this useEffect.
  useEffect(() => {
    const token = authService.getToken();
    if (!token) return;

    const socket = io(SOCKET_IO_URL, {
      path: SOCKET_IO_PATH,
      auth: { token },
      transports: SOCKET_IO_TRANSPORTS,
      withCredentials: true,
    });
    socketRef.current = socket;

    const onConnect = () => socket.emit('join-all-chat-channels');
    socket.on('connect', onConnect);

    socket.on('chat:message', (payload: any) => {
      const channelId = payload?.channelId ?? (payload as any)?.channelId;
      if (channelId) {
        globalMutate(
          (key) =>
            typeof key === 'string' &&
            (key === '/chat/channels' || key.startsWith('/chat/channels/')),
          undefined,
          { revalidate: true }
        );

        const isAppHidden = typeof document !== 'undefined' && document.hidden;
        const panelOpen = isPanelOpenRef.current;
        const currentChannelId = selectedChannelIdRef.current;
        const shouldNotify =
          isAppHidden || !panelOpen || currentChannelId !== channelId;

        if (shouldNotify) {
          const senderName = senderNameFromPayload(payload);
          const content = payload?.content ?? '';
          const contentPreview = typeof content === 'string' ? content.replace(/<[^>]+>/g, '').trim().slice(0, 60) : '';
          const item: ChatNotificationItem = {
            id: `chat-${channelId}-${Date.now()}`,
            channelId,
            senderName,
            contentPreview,
            createdAt: payload?.createdAt ?? new Date().toISOString(),
          };
          setNotifications((prev) => [...prev.slice(-4), item]);

          if (isAppHidden && typeof Notification !== 'undefined') {
            if (Notification.permission === 'granted') {
              try {
                new Notification(`New message from ${senderName}`, {
                  body: contentPreview || 'New chat message',
                  tag: `chat-${channelId}`,
                });
              } catch (_) {}
            } else if (Notification.permission === 'default') {
              Notification.requestPermission();
            }
          }
        }
      }
    });

    socket.on('chat:unread', () => {
      globalMutate(
        (key) => typeof key === 'string' && key === '/chat/channels',
        undefined,
        { revalidate: true }
      );
    });

    return () => {
      socket.off('connect', onConnect);
      socket.off('chat:message');
      socket.off('chat:unread');
      socketRef.current = null;
      if (socket.connected) socket.disconnect();
    };
  }, [globalMutate]);
  */

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const setSelectedChannel = useCallback((id: number | null) => setSelectedChannelIdState(id), []);

  const handleOpenChatFromNotification = useCallback((channelId: number) => {
    setSelectedChannelIdState(channelId);
    setIsPanelOpen(true);
  }, []);

  const handleDismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const joinChannel = useCallback((channelId: number) => {
    // Socket.IO disabled: would emit 'join-chat-channel'
    // const s = socketRef.current;
    // if (s) s.emit('join-chat-channel', { channelId });
  }, []);

  const leaveChannel = useCallback((channelId: number) => {
    // Socket.IO disabled: would emit 'leave-chat-channel'
    // const s = socketRef.current;
    // if (s) s.emit('leave-chat-channel', { channelId });
  }, []);

  const value: ChatContextType = {
    isPanelOpen,
    openPanel,
    closePanel,
    selectedChannelId,
    setSelectedChannel,
    joinChannel,
    leaveChannel,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
      <ChatNotificationToast
        notifications={notifications}
        onOpenChat={handleOpenChatFromNotification}
        onDismiss={handleDismissNotification}
      />
    </ChatContext.Provider>
  );
};

export function useChat(): ChatContextType {
  const ctx = useContext(ChatContext);
  if (ctx === undefined) throw new Error('useChat must be used within ChatProvider');
  return ctx;
}
